import { useEffect, useMemo, useState } from 'react';
import { collection, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { firestoreCache, CACHE_KEYS, CACHE_TTL } from '../utils/firestoreCache';
import { guardedGetDocs } from '../utils/guardedFirestore';
import { getOfferingKey } from '../utils/courseIdentity';

interface UseLastYearEnrollmentResult {
  count: number | null;
  year: number;
  loading: boolean;
}

const MIN_DAYS_THRESHOLD = 50;
const MISSING_DATA_YEAR = 2025;

const parseAcademicYear = (value: string | number | undefined): number | null => {
  if (!value) return null;
  const text = String(value);
  const digits = text.replace(/[^0-9]/g, '');
  if (!digits) return null;
  const parsed = Number(digits.slice(0, 4));
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * 昨年の履修者数を計算するフック
 * enrollmentHistory（削除済み履修）と現在のenrollmentsから、
 * 特定のコースを50日以上表示していたユニークユーザー数を算出
 */
export const useLastYearEnrollment = (courseId: string | undefined, courseAcademicYear?: string): UseLastYearEnrollmentResult => {
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchLastYearEnrollment = async () => {
      if (!courseId) {
        setCount(null);
        setLoading(false);
        return;
      }

      const targetYear = (parseAcademicYear(courseAcademicYear) || new Date().getFullYear()) - 1;

      if (targetYear === MISSING_DATA_YEAR) {
        if (!cancelled) {
          setCount(null);
          setLoading(false);
        }
        return;
      }

      // --- キャッシュヒット確認 ---
      const cacheKey = `${CACHE_KEYS.lastYearEnrollment(getOfferingKey({ id: courseId, academicYear: courseAcademicYear }))}:${targetYear}`;
      const cached = firestoreCache.get<number>(cacheKey);
      if (cached !== null) {
        if (!cancelled) {
          setCount(cached);
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);

        const uniqueUserIds = new Set<string>();

        // 1. enrollmentHistoryから取得（削除済み履修）
        const historyQuery = query(
          collection(db, 'enrollmentHistory'),
          where('courseId', '==', courseId)
        );
        const historySnap = await guardedGetDocs(historyQuery);

        if (!historySnap) {
          // クォータ超過またはサーキット遮断
          if (!cancelled) setLoading(false);
          return;
        }

        historySnap.forEach((docSnap) => {
          const data = docSnap.data();
          const createdAt = data.createdAt;
          const removedAt = data.removedAt;

          if (createdAt && removedAt) {
            const daysEnrolled = (removedAt - createdAt) / (1000 * 60 * 60 * 24);
            if (daysEnrolled >= MIN_DAYS_THRESHOLD) {
              uniqueUserIds.add(data.userId);
            }
          }
        });

        // 2. 現在のenrollmentsから取得（まだ削除されていない履修）
        const currentQuery = query(
          collection(db, 'enrollments'),
          where('courseId', '==', courseId)
        );
        const currentSnap = await guardedGetDocs(currentQuery);

        if (!currentSnap) {
          if (!cancelled) setLoading(false);
          return;
        }

        const now = Date.now();
        currentSnap.forEach((docSnap) => {
          const data = docSnap.data();
          const createdAt = data.createdAt;

          if (createdAt) {
            const daysEnrolled = (now - createdAt) / (1000 * 60 * 60 * 24);
            if (daysEnrolled >= MIN_DAYS_THRESHOLD) {
              uniqueUserIds.add(data.userId);
            }
          }
        });

        // キャッシュに保存
        const result = uniqueUserIds.size;
        firestoreCache.set(cacheKey, result, CACHE_TTL.LAST_YEAR_ENROLLMENT);

        if (!cancelled) {
          setCount(result);
        }
      } catch (error) {
        console.error('[useLastYearEnrollment] Failed to fetch enrollment history:', error);
        if (!cancelled) {
          setCount(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchLastYearEnrollment();

    return () => {
      cancelled = true;
    };
  }, [courseId, courseAcademicYear]);

  const year = useMemo(() => {
    return (parseAcademicYear(courseAcademicYear) || new Date().getFullYear()) - 1;
  }, [courseAcademicYear]);

  return { count, year, loading };
};
